import type { VerticalJustification } from "building-types/datums/datumsTypesEnum";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ProfileId } from "building-types/profiles/profileTypes";
import type { RoofPerimeterRail } from "building-types/roofs/roofPerimeterTypes";
import type { FriezeBoard } from "building-types/settings/roofBaseTypes";
import type { RoofSide } from "building-types/settings/roofTypes";
import type { EaveType, WrapFriezeBoardMode } from "building-types/settings/settingsTypesEnum";
import type { Face } from "geom/brep";
import type { Plane } from "geom/planeTypes";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import type { BBox, RBush3D } from "rbush-3d";

export const ROOF_RAIL_RTREE_BOX_HALF_WIDTH = 0.5;
export const ROOF_RAIL_RTREE_BOX_HALF_WIDTH_FOR_INTERSECTION = 120;
export const ROOF_RAIL_COLLINEARITY_TOLERANCE = 0.1;

export type RoofTrimRailTransformer<T> = (roofTrimRail: T, rtree: RBush3D) => T;
export type RoofTrimRailConstructor<T> = (roofPerimeterRail: RoofPerimeterRail, groupId: string) => T[];
export type RoofTrimRailGetExtraRails<T> = (subRoofId: string) => T[];
export type RoofTrimRailFilter = (roofPerimeterRail: RoofPerimeterRail) => boolean;
export type RoofTrimRailGetKey = (roofPerimeterRail: RoofPerimeterRail) => string;

export interface BellyBandBoard {
  readonly plane: Plane;
  readonly rail: PolyLine3;
  readonly profileId: ProfileId;
  readonly material: ExteriorMaterial;
  readonly justification: VerticalJustification;
  readonly eaveType: EaveType;
  readonly startOverhang: number;
  readonly endOverhang: number;
  readonly startBaseHeight: number;
  readonly endBaseHeight: number;
  readonly roofSide: RoofSide;
}

export type ConnectedRoofTrimRailFilter = (
  roofTrimRail: RoofTrimRail,
  otherRoofTrimRail: RoofTrimRail,
  isEnd?: boolean,
  isOtherEnd?: boolean,
) => boolean;

export enum RoofTrimRailType {
  VOID = "VOID",
  FASCIA = "FASCIA",
  FASCIA_TRIM = "FASCIA_TRIM",
  FRIEZE_BOARD = "FRIEZE_BOARD",
  SOFFIT = "SOFFIT",
}

export interface FriezePlaneGroup {
  readonly verticalPlane?: Plane;
  readonly horizontalPlane?: Plane;
}

export interface RoofTrimRailBaseType<T extends RoofTrimRailType> {
  readonly type: T;
}

export interface RoofTrimRailBase<T extends RoofTrimRailType> extends RoofTrimRailBaseType<T> {
  readonly rail: PolyLine3;
  readonly roofPerimeterRail: RoofPerimeterRail;
  readonly groupId: string;
  readonly material?: ExteriorMaterial;
}

export type RoofTrimRailVoid = RoofTrimRailBaseType<RoofTrimRailType.VOID>;

export interface FasciaRail extends RoofTrimRailBase<RoofTrimRailType.FASCIA> {
  readonly boardSize: number;
  readonly prevBoardSize: number;
  readonly nextBoardSize: number;
  readonly boardThickness: number;
  readonly adjustSize: boolean;
}

export interface FasciaTrimRail extends RoofTrimRailBase<RoofTrimRailType.FASCIA_TRIM> {
  readonly boardSize: number;
  readonly prevBoardSize: number;
  readonly nextBoardSize: number;
  readonly boardThickness: number;
  readonly adjustSize: boolean;
}

export interface FriezeBoardRail extends RoofTrimRailBase<RoofTrimRailType.FRIEZE_BOARD> {
  readonly startWrapMode: WrapFriezeBoardMode;
  readonly endWrapMode: WrapFriezeBoardMode;
  readonly friezeBoard: string;
  readonly friezePlane: Plane;
  readonly extraWallLikeFaces: Face[];
  readonly startClipPlanes: FriezePlaneGroup | undefined;
  readonly endClipPlanes: FriezePlaneGroup | undefined;
  readonly friezeBoardVerticalOffset?: number;
  readonly friezeBoardLateralOffset?: number;
}

export interface SoffitRail extends RoofTrimRailBase<RoofTrimRailType.SOFFIT> {
  readonly soffitHandle: DocEntityHandle;
  readonly outerPlane: Plane;
  readonly midPlane: Plane;
  readonly innerPlane: Plane;
  readonly fasciaTopRail: PolyLine2;
  readonly outerTopRail: PolyLine2;
  readonly outerBottomRail: PolyLine2;
  readonly midBottomRail: PolyLine2;
  readonly innerTopRail: PolyLine2;
  readonly innerBottomRail: PolyLine2;
  readonly soffitSize: number;
}

export type RoofTrimRail = FasciaRail | FasciaTrimRail | FriezeBoardRail | SoffitRail | RoofTrimRailVoid;

export interface RoofTrimRailBox extends BBox {
  readonly roofTrimRail: RoofTrimRail;
  readonly isEnd: boolean;
}

export function friezePlaneGroupNew(verticalPlane?: Plane, horizontalPlane?: Plane): FriezePlaneGroup {
  return {
    verticalPlane,
    horizontalPlane,
  };
}

export function roofTrimRailVoidNew(): RoofTrimRailVoid {
  return {
    type: RoofTrimRailType.VOID,
  };
}

export function roofFasciaRailNew(
  rail: PolyLine3,
  roofPerimeterRail: RoofPerimeterRail,
  boardSize: number,
  prevBoardSize: number,
  nextBoardSize: number,
  boardThickness: number,
  adjustSize: boolean,
  groupId: string,
  fasciaMaterial?: ExteriorMaterial,
): FasciaRail {
  return {
    type: RoofTrimRailType.FASCIA,
    rail,
    roofPerimeterRail,
    groupId,
    boardSize,
    prevBoardSize,
    nextBoardSize,
    boardThickness,
    adjustSize,
    material: fasciaMaterial,
  };
}

export function roofFasciaTrimRailNew(
  rail: PolyLine3,
  roofPerimeterRail: RoofPerimeterRail,
  boardSize: number,
  prevBoardSize: number,
  nextBoardSize: number,
  boardThickness: number,
  groupId: string,
  fasciaTrimMaterial?: ExteriorMaterial,
): FasciaTrimRail {
  return {
    type: RoofTrimRailType.FASCIA_TRIM,
    rail,
    roofPerimeterRail,
    groupId,
    boardSize,
    prevBoardSize,
    nextBoardSize,
    boardThickness,
    adjustSize: false,
    material: fasciaTrimMaterial,
  };
}

export function roofFriezeBoardRailNew(
  rail: PolyLine3,
  roofPerimeterRail: RoofPerimeterRail,
  friezePlane: Plane,
  friezeBoard: string,
  startWrapMode: WrapFriezeBoardMode,
  endWrapMode: WrapFriezeBoardMode,
  groupId: string,
  friezeBoardMaterial?: ExteriorMaterial,
  startClipPlanes?: FriezePlaneGroup,
  endClipPlanes?: FriezePlaneGroup,
  extraWallLikeFaces: Face[] = [],
  friezeBoardVerticalOffset?: number,
  friezeBoardLateralOffset?: number,
): FriezeBoardRail {
  return {
    type: RoofTrimRailType.FRIEZE_BOARD,
    rail,
    roofPerimeterRail,
    groupId,
    friezePlane,
    friezeBoard,
    material: friezeBoardMaterial,
    startWrapMode,
    endWrapMode,
    startClipPlanes,
    endClipPlanes,
    extraWallLikeFaces,
    friezeBoardVerticalOffset,
    friezeBoardLateralOffset,
  };
}

export function roofSoffitRailNew(
  rail: PolyLine3,
  roofPerimeterRail: RoofPerimeterRail,
  outerPlane: Plane,
  midPlane: Plane,
  innerPlane: Plane,
  fasciaTopRail: PolyLine2,
  outerTopRail: PolyLine2,
  outerBottomRail: PolyLine2,
  midBottomRail: PolyLine2,
  innerTopRail: PolyLine2,
  innerBottomRail: PolyLine2,
  soffitSize: number,
  groupId: string,
  soffitHandle: DocEntityHandle,
  soffitMaterial?: ExteriorMaterial,
): SoffitRail {
  return {
    type: RoofTrimRailType.SOFFIT,
    soffitHandle,
    rail,
    roofPerimeterRail,
    groupId,
    outerPlane,
    midPlane,
    innerPlane,
    fasciaTopRail,
    outerTopRail,
    outerBottomRail,
    midBottomRail,
    innerTopRail,
    innerBottomRail,
    soffitSize,
    material: soffitMaterial,
  };
}

export function isRoofTrimRailTypeFrieze(rail: RoofTrimRail): rail is FriezeBoardRail {
  return rail?.type === RoofTrimRailType.FRIEZE_BOARD;
}

export function isRoofTrimRailTypeSoffit(rail: RoofTrimRail): rail is SoffitRail {
  return rail?.type === RoofTrimRailType.SOFFIT;
}

export function isRoofTrimRailTypeVoid(rail: RoofTrimRail): rail is RoofTrimRailVoid {
  return rail?.type === RoofTrimRailType.VOID;
}

export function isRoofTrimRailTypeFascia(rail: RoofTrimRail): rail is FasciaRail {
  return rail?.type === RoofTrimRailType.FASCIA;
}

export function isRoofTrimRailTypeFasciaTrim(rail: RoofTrimRail): rail is FasciaTrimRail {
  return rail?.type === RoofTrimRailType.FASCIA_TRIM;
}

export function isNonVoidRoofTrimRailType(
  rail: RoofTrimRail,
): rail is FasciaRail | FasciaTrimRail | FriezeBoardRail | SoffitRail {
  return rail?.type !== RoofTrimRailType.VOID;
}

export function roofTrimRailBoxNew(bbox: BBox, roofTrimRail: RoofTrimRail, isEnd: boolean): RoofTrimRailBox {
  return {
    ...bbox,
    roofTrimRail,
    isEnd,
  };
}

export function bellyBandBoardNew(
  plane: Plane,
  rail: PolyLine3,
  profileId: ProfileId,
  material: ExteriorMaterial,
  justification: VerticalJustification,
  eaveType: EaveType,
  startOverhang: number,
  endOverhang: number,
  startBaseHeight: number,
  endBaseHeight: number,
  roofSide: RoofSide,
): BellyBandBoard {
  return {
    plane,
    rail,
    profileId,
    material,
    justification,
    eaveType,
    startOverhang,
    endOverhang,
    startBaseHeight,
    endBaseHeight,
    roofSide,
  };
}

export function friezeBoardNew(
  friezeBoard: string,
  name: string,
  friezeBoardMaterial?: ExteriorMaterial,
  verticalOffset?: number,
  lateralOffset?: number,
): FriezeBoard {
  return {
    friezeBoard,
    name,
    friezeBoardMaterial,
    verticalOffset,
    lateralOffset,
  };
}
